<template>
  <b-container fluid>
    <b-row class="mt-3">
      <b-col cols="12" class="d-flex flex-row">
        <h1 class="font-weight-bold mb-0 text-primary flex-fill">Marketing</h1>
        <b-button variant="outline-primary" class="align-self-center" v-b-modal.google-call-log-modal>
          Upload Google Call Log
        </b-button>
      </b-col>
      <b-col cols="12" md="3" class="mt-3">
        <label class="text-muted mb-1">Marketing Year</label>
        <b-form-select v-model="marketingYear" :options="yearOptions" @change="fetchMarketing"></b-form-select>
      </b-col>
    </b-row>

    <b-modal id="google-call-log-modal" title="Google Call Log Attribution" centered @ok.prevent="uploadGoogleCallLog" :ok-disabled="!googleCallLogFile || isUploadingGoogleCallLog" ok-title="Upload & Match">
      <p class="text-muted">Upload Google call reports to automatically match leads and sales.</p>
      <b-form-file v-model="googleCallLogFile" accept=".csv,text/csv" placeholder="Choose Google call log CSV"></b-form-file>
      <small class="text-muted d-block mt-2">Matches Google ad calls to customer phone prefixes and lead dates.</small>
      <b-alert v-if="googleCallLogResult" show variant="success" class="mt-3 mb-0">
        Updated {{ googleCallLogResult.updated_leads }} leads to Google, including {{ googleCallLogResult.updated_sales_count }} sales worth {{ googleCallLogResult.updated_revenue | currencyFormat }}.
        Matched against {{ googleCallLogResult.matchable_ad_calls }} Google ad calls.
      </b-alert>
      <template #modal-ok>
        <b-spinner v-if="isUploadingGoogleCallLog" small class="mr-1"></b-spinner>
        Upload & Match
      </template>
    </b-modal>

    <b-row class="my-3">
      <b-col cols="12" lg="6" class="mb-3 mb-lg-0">
        <b-card class="shadow-sm rounded">
          <b-card-body class="d-flex flex-column align-items-center justify-content-center">
            <div class="w-100 mb-4 d-flex flex-column flex-md-row">
              <div class="flex-fill">
                <h5 class="text-muted mb-1">Cumulative</h5>
                <small class="text-muted">Running ad spend, total revenue, leads and sales, including the current month to date.</small>
              </div>
              <b-button-group size="sm" class="mt-2 mt-md-0">
                <b-button :variant="marketingCumulativeView === 'chart' ? 'primary' : 'outline-secondary'" @click="setMarketingCumulativeView('chart')">Chart</b-button>
                <b-button :variant="marketingCumulativeView === 'table' ? 'primary' : 'outline-secondary'" @click="setMarketingCumulativeView('table')">Table</b-button>
              </b-button-group>
            </div>
            <canvas v-show="marketingCumulativeView === 'chart'" ref="marketingCumulativeChart" id="marketing-cumulative-chart" width="400" height="220"></canvas>
            <p v-if="marketingCumulativeView === 'table' && hasPartialTrendMonth && hasTrendComparison" class="text-primary small mb-2 w-100">
              Highlighted row compares to the same point in the month last year.
            </p>
            <b-table-simple v-if="marketingCumulativeView === 'table'" responsive hover class="mb-0 small">
              <b-thead>
                <b-tr>
                  <b-th class="p-3">Month</b-th>
                  <b-th class="text-right p-3">Spend</b-th>
                  <b-th v-if="hasTrendComparison" class="text-right p-3">Spend vs {{ marketing.trend.previous_year }}</b-th>
                  <b-th v-if="hasTrendComparison" class="text-right p-3">Spend %</b-th>
                  <b-th class="text-right p-3">Revenue</b-th>
                  <b-th v-if="hasTrendComparison" class="text-right p-3">Revenue vs {{ marketing.trend.previous_year }}</b-th>
                  <b-th v-if="hasTrendComparison" class="text-right p-3">Revenue %</b-th>
                  <b-th class="text-right p-3">Leads</b-th>
                  <b-th class="text-right p-3">Sales</b-th>
                </b-tr>
              </b-thead>
              <b-tbody v-if="!isLoading && trendRows.length">
                <b-tr v-for="row in trendRows" :key="`cumulative-${row.month}`" :class="{ 'partial-month-row': row.partial }">
                  <b-td class="p-3">{{ row.month }}</b-td>
                  <b-td class="text-right p-3">{{ row.cumulativeSpend | currencyFormat }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatCurrencyChange(row.cumulativeSpendChange) }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatPercentageChange(row.cumulativeSpendChangePercent) }}</b-td>
                  <b-td class="text-right p-3">{{ row.cumulativeRevenue | currencyFormat }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatCurrencyChange(row.cumulativeRevenueChange) }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatPercentageChange(row.cumulativeRevenueChangePercent) }}</b-td>
                  <b-td class="text-right p-3">{{ row.cumulativeLeads }}</b-td>
                  <b-td class="text-right p-3">{{ row.cumulativeSales }}</b-td>
                </b-tr>
              </b-tbody>
              <b-tfoot v-if="!isLoading && trendRows.length">
                <b-tr class="font-weight-bold">
                  <b-td class="p-3">Year to Date</b-td>
                  <b-td class="text-right p-3">{{ cumulativeTrendTotals.spend | currencyFormat }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatCurrencyChange(cumulativeTrendTotals.spendChange) }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatPercentageChange(cumulativeTrendTotals.spendChangePercent) }}</b-td>
                  <b-td class="text-right p-3">{{ cumulativeTrendTotals.revenue | currencyFormat }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatCurrencyChange(cumulativeTrendTotals.revenueChange) }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatPercentageChange(cumulativeTrendTotals.revenueChangePercent) }}</b-td>
                  <b-td class="text-right p-3">{{ cumulativeTrendTotals.leads }}</b-td>
                  <b-td class="text-right p-3">{{ cumulativeTrendTotals.sales }}</b-td>
                </b-tr>
              </b-tfoot>
              <b-tbody v-if="!isLoading && !trendRows.length">
                <b-tr><b-td :colspan="hasTrendComparison ? 9 : 5" class="p-3 text-center">There is no cumulative trend data for this year.</b-td></b-tr>
              </b-tbody>
            </b-table-simple>
            <b-spinner v-if="isLoading" variant="secondary" class="mt-1"></b-spinner>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col cols="12" lg="6">
        <b-card class="shadow-sm rounded">
          <b-card-body class="d-flex flex-column align-items-center justify-content-center">
            <div class="w-100 mb-4 d-flex flex-column flex-md-row">
              <div class="flex-fill">
                <h5 class="text-muted mb-1">Month by Month</h5>
                <small class="text-muted">Monthly ad spend, total revenue, leads and sales, including the current month to date.</small>
              </div>
              <b-button-group size="sm" class="mt-2 mt-md-0">
                <b-button :variant="marketingMonthlyView === 'chart' ? 'primary' : 'outline-secondary'" @click="setMarketingMonthlyView('chart')">Chart</b-button>
                <b-button :variant="marketingMonthlyView === 'table' ? 'primary' : 'outline-secondary'" @click="setMarketingMonthlyView('table')">Table</b-button>
              </b-button-group>
            </div>
            <canvas v-show="marketingMonthlyView === 'chart'" ref="marketingMonthlyChart" id="marketing-monthly-chart" width="400" height="220"></canvas>
            <p v-if="marketingMonthlyView === 'table' && hasPartialTrendMonth && hasTrendComparison" class="text-primary small mb-2 w-100">
              Highlighted row compares to the same point in the month last year.
            </p>
            <b-table-simple v-if="marketingMonthlyView === 'table'" responsive hover class="mb-0 small">
              <b-thead>
                <b-tr>
                  <b-th class="p-3">Month</b-th>
                  <b-th class="text-right p-3">Spend</b-th>
                  <b-th v-if="hasTrendComparison" class="text-right p-3">Spend vs {{ marketing.trend.previous_year }}</b-th>
                  <b-th v-if="hasTrendComparison" class="text-right p-3">Spend %</b-th>
                  <b-th class="text-right p-3">Revenue</b-th>
                  <b-th v-if="hasTrendComparison" class="text-right p-3">Revenue vs {{ marketing.trend.previous_year }}</b-th>
                  <b-th v-if="hasTrendComparison" class="text-right p-3">Revenue %</b-th>
                  <b-th class="text-right p-3">Leads</b-th>
                  <b-th class="text-right p-3">Sales</b-th>
                </b-tr>
              </b-thead>
              <b-tbody v-if="!isLoading && trendRows.length">
                <b-tr v-for="row in trendRows" :key="`monthly-${row.month}`" :class="{ 'partial-month-row': row.partial }">
                  <b-td class="p-3">{{ row.month }}</b-td>
                  <b-td class="text-right p-3">{{ row.spend | currencyFormat }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatCurrencyChange(row.spendChange) }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatPercentageChange(row.spendChangePercent) }}</b-td>
                  <b-td class="text-right p-3">{{ row.revenue | currencyFormat }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatCurrencyChange(row.revenueChange) }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatPercentageChange(row.revenueChangePercent) }}</b-td>
                  <b-td class="text-right p-3">{{ row.leads }}</b-td>
                  <b-td class="text-right p-3">{{ row.sales }}</b-td>
                </b-tr>
              </b-tbody>
              <b-tfoot v-if="!isLoading && trendRows.length">
                <b-tr class="font-weight-bold">
                  <b-td class="p-3">Year to Date</b-td>
                  <b-td class="text-right p-3">{{ trendTotals.spend | currencyFormat }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatCurrencyChange(trendTotals.spendChange) }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatPercentageChange(trendTotals.spendChangePercent) }}</b-td>
                  <b-td class="text-right p-3">{{ trendTotals.revenue | currencyFormat }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatCurrencyChange(trendTotals.revenueChange) }}</b-td>
                  <b-td v-if="hasTrendComparison" class="text-right p-3">{{ formatPercentageChange(trendTotals.revenueChangePercent) }}</b-td>
                  <b-td class="text-right p-3">{{ trendTotals.leads }}</b-td>
                  <b-td class="text-right p-3">{{ trendTotals.sales }}</b-td>
                </b-tr>
              </b-tfoot>
              <b-tbody v-if="!isLoading && !trendRows.length">
                <b-tr><b-td :colspan="hasTrendComparison ? 9 : 5" class="p-3 text-center">There is no monthly trend data for this year.</b-td></b-tr>
              </b-tbody>
            </b-table-simple>
            <b-spinner v-if="isLoading" variant="secondary" class="mt-1"></b-spinner>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="my-3">
      <b-col cols="12" lg="6" class="mb-3 mb-lg-0">
        <b-card class="shadow-sm rounded">
          <b-card-body class="d-flex flex-column align-items-center justify-content-center">
            <div class="w-100 mb-4 d-flex flex-column flex-md-row">
              <div class="flex-fill">
                <h5 class="text-muted mb-1">Revenue by Product Group</h5>
                <small class="text-muted">Closed order revenue for the selected year, grouped by product category.</small>
              </div>
              <b-button-group size="sm" class="mt-2 mt-md-0">
                <b-button :variant="productCategoryRevenueView === 'chart' ? 'primary' : 'outline-secondary'" @click="setProductCategoryRevenueView('chart')">Chart</b-button>
                <b-button :variant="productCategoryRevenueView === 'table' ? 'primary' : 'outline-secondary'" @click="setProductCategoryRevenueView('table')">Table</b-button>
              </b-button-group>
            </div>
            <canvas v-show="productCategoryRevenueView === 'chart'" ref="productCategoryRevenueChart" id="product-category-revenue-chart" class="product-category-chart" width="400" height="160"></canvas>
            <b-table-simple v-if="productCategoryRevenueView === 'table'" responsive hover class="mb-0">
              <b-thead>
                <b-tr>
                  <b-th class="p-3">Product Group</b-th>
                  <b-th class="text-right p-3">Revenue</b-th>
                  <b-th class="text-right p-3">Share</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="row in productCategoryRevenueRows" :key="`revenue${row.label}`">
                  <b-td class="p-3">{{ row.label }}</b-td>
                  <b-td class="text-right p-3">{{ row.value | currencyFormat }}</b-td>
                  <b-td class="text-right p-3">{{ row.percentage }}%</b-td>
                </b-tr>
              </b-tbody>
              <b-tfoot v-if="productCategoryRevenueRows.length">
                <b-tr class="font-weight-bold">
                  <b-td class="p-3">Total</b-td>
                  <b-td class="text-right p-3">{{ productCategoryRevenueTotal | currencyFormat }}</b-td>
                  <b-td class="text-right p-3">100.0%</b-td>
                </b-tr>
              </b-tfoot>
            </b-table-simple>
            <b-spinner v-if="isLoading" variant="secondary" class="mt-1"></b-spinner>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col cols="12" lg="6">
        <b-card class="shadow-sm rounded">
          <b-card-body class="d-flex flex-column align-items-center justify-content-center">
            <div class="w-100 mb-4 d-flex flex-column flex-md-row">
              <div class="flex-fill">
                <h5 class="text-muted mb-1">Sales Count by Product Group</h5>
                <small class="text-muted">Closed order count for the selected year, grouped by product category.</small>
              </div>
              <b-button-group size="sm" class="mt-2 mt-md-0">
                <b-button :variant="productCategorySalesView === 'chart' ? 'primary' : 'outline-secondary'" @click="setProductCategorySalesView('chart')">Chart</b-button>
                <b-button :variant="productCategorySalesView === 'table' ? 'primary' : 'outline-secondary'" @click="setProductCategorySalesView('table')">Table</b-button>
              </b-button-group>
            </div>
            <canvas v-show="productCategorySalesView === 'chart'" ref="productCategorySalesChart" id="product-category-sales-chart" class="product-category-chart" width="400" height="160"></canvas>
            <b-table-simple v-if="productCategorySalesView === 'table'" responsive hover class="mb-0">
              <b-thead>
                <b-tr>
                  <b-th class="p-3">Product Group</b-th>
                  <b-th class="text-right p-3">Sales</b-th>
                  <b-th class="text-right p-3">Share</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="row in productCategorySalesRows" :key="`sales${row.label}`">
                  <b-td class="p-3">{{ row.label }}</b-td>
                  <b-td class="text-right p-3">{{ row.value }}</b-td>
                  <b-td class="text-right p-3">{{ row.percentage }}%</b-td>
                </b-tr>
              </b-tbody>
              <b-tfoot v-if="productCategorySalesRows.length">
                <b-tr class="font-weight-bold">
                  <b-td class="p-3">Total</b-td>
                  <b-td class="text-right p-3">{{ productCategorySalesTotal }}</b-td>
                  <b-td class="text-right p-3">100.0%</b-td>
                </b-tr>
              </b-tfoot>
            </b-table-simple>
            <b-spinner v-if="isLoading" variant="secondary" class="mt-1"></b-spinner>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="my-3">
      <b-col cols="12">
        <b-tabs content-class="mt-3">
          <b-tab title="Leads" title-item-class="order-2">
            <b-card class="mb-3">
              <b-card-body class="p-1">
                <h4 class="text-muted mb-1">Lead Summary</h4>
                <p class="text-muted mb-3">Leads grouped by their credited source, with paid spend and revenue generated so far.</p>
                <b-table-simple responsive hover>
                  <b-thead>
                    <b-tr>
                      <b-th class="p-3">Credited Source</b-th>
                      <b-th class="p-3">Leads</b-th>
                      <b-th class="p-3">Sales (so far)</b-th>
                      <b-th class="text-right p-3">Spend</b-th>
                      <b-th class="text-right p-3">Revenue (so far)</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody v-if="!isLoading && marketing.lead_summary && marketing.lead_summary.length">
                    <b-tr v-for="(row, ind) in marketing.lead_summary" :key="`mls${ind}`">
                      <b-td class="p-3">{{ row.source }}</b-td>
                      <b-td class="p-3">{{ row.leads_count }}</b-td>
                      <b-td class="p-3">{{ row.sales_count }}</b-td>
                      <b-td class="text-right p-3">
                        <span v-if="row.is_ad_source">{{ row.spend | currencyFormat }}</span>
                        <span v-else class="text-muted">-</span>
                      </b-td>
                      <b-td class="text-right p-3">{{ row.revenue | currencyFormat }}</b-td>
                    </b-tr>
                  </b-tbody>
                  <b-tfoot v-if="!isLoading && marketing.lead_summary && marketing.lead_summary.length">
                    <b-tr class="font-weight-bold">
                      <b-td class="p-3">Total</b-td>
                      <b-td class="p-3">{{ leadSummaryTotals.leads }}</b-td>
                      <b-td class="p-3">{{ leadSummaryTotals.sales }}</b-td>
                      <b-td class="text-right p-3">{{ leadSummaryTotals.spend | currencyFormat }}</b-td>
                      <b-td class="text-right p-3">{{ leadSummaryTotals.revenue | currencyFormat }}</b-td>
                    </b-tr>
                  </b-tfoot>
                  <b-tbody v-if="!isLoading && (!marketing.lead_summary || !marketing.lead_summary.length)">
                    <b-tr><b-td colspan="5" class="p-3 text-center">There are no leads in this range.</b-td></b-tr>
                  </b-tbody>
                  <b-tbody v-if="isLoading">
                    <b-tr>
                      <b-td colspan="5">
                        <div class="d-flex justify-content-center"><b-spinner variant="secondary"/></div>
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </b-card-body>
            </b-card>

            <b-card>
              <b-card-body class="p-1">
                <h4 class="text-muted mb-1">Lead Detail</h4>
                <p class="text-muted mb-3">Individual leads for the selected year, including their source, status and any resulting revenue.</p>
                <b-table-simple responsive hover>
                  <b-thead>
                    <b-tr>
                      <b-th class="p-3">Customer</b-th>
                      <b-th class="p-3">Credited Source</b-th>
                      <b-th class="p-3">Status</b-th>
                      <b-th class="p-3">Lead Date</b-th>
                      <b-th class="p-3">Products Bought</b-th>
                      <b-th class="text-right p-3">Revenue</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody v-if="!isLoading && marketing.leads && marketing.leads.length">
                    <b-tr v-for="(lead, ind) in marketing.leads" :key="`ml${ind}`">
                      <b-td class="p-3">
                        <router-link :to="{name: 'dash.customers.view', params: { id: lead.id }}">{{ lead.name }}</router-link>
                      </b-td>
                      <b-td class="p-3">{{ lead.source }}</b-td>
                      <b-td class="p-3">{{ lead.status || '-' }}</b-td>
                      <b-td class="p-3">{{ moment(lead.lead_at).format('DD/MM/YYYY') }}</b-td>
                      <b-td class="p-3">
                        <span v-if="lead.products && lead.products.length">{{ lead.products.join(', ') }}</span>
                        <span v-else class="text-muted">Not yet purchased</span>
                      </b-td>
                      <b-td class="text-right p-3">{{ lead.revenue | currencyFormat }}</b-td>
                    </b-tr>
                  </b-tbody>
                  <b-tfoot v-if="!isLoading && marketing.leads && marketing.leads.length">
                    <b-tr class="font-weight-bold">
                      <b-td colspan="5" class="p-3">Total</b-td>
                      <b-td class="text-right p-3">{{ leadDetailRevenueTotal | currencyFormat }}</b-td>
                    </b-tr>
                  </b-tfoot>
                  <b-tbody v-if="!isLoading && (!marketing.leads || !marketing.leads.length)">
                    <b-tr><b-td colspan="6" class="p-3 text-center">There are no leads in this range.</b-td></b-tr>
                  </b-tbody>
                  <b-tbody v-if="isLoading">
                    <b-tr>
                      <b-td colspan="6">
                        <div class="d-flex justify-content-center"><b-spinner variant="secondary"/></div>
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </b-card-body>
            </b-card>
          </b-tab>

          <b-tab title="Sales" active title-item-class="order-1">
            <b-card class="mb-3">
              <b-card-body class="p-1">
                <h4 class="text-muted mb-1">Sales Summary</h4>
                <p class="text-muted mb-3">Closed sales grouped by their credited source, with paid spend and total revenue.</p>
                <b-table-simple responsive hover>
                  <b-thead>
                    <b-tr>
                      <b-th class="p-3">Credited Source</b-th>
                      <b-th class="p-3">Sales</b-th>
                      <b-th class="text-right p-3">Spend</b-th>
                      <b-th class="text-right p-3">Revenue</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody v-if="!isLoading && marketing.sales_summary && marketing.sales_summary.length">
                    <b-tr v-for="(row, ind) in marketing.sales_summary" :key="`mss${ind}`">
                      <b-td class="p-3">{{ row.source }}</b-td>
                      <b-td class="p-3">{{ row.sales_count }}</b-td>
                      <b-td class="text-right p-3">
                        <span v-if="row.is_ad_source">{{ row.spend | currencyFormat }}</span>
                        <span v-else class="text-muted">-</span>
                      </b-td>
                      <b-td class="text-right p-3">{{ row.revenue | currencyFormat }}</b-td>
                    </b-tr>
                  </b-tbody>
                  <b-tfoot v-if="!isLoading && marketing.sales_summary && marketing.sales_summary.length">
                    <b-tr class="font-weight-bold">
                      <b-td class="p-3">Total</b-td>
                      <b-td class="p-3">{{ salesSummaryTotals.sales }}</b-td>
                      <b-td class="text-right p-3">{{ salesSummaryTotals.spend | currencyFormat }}</b-td>
                      <b-td class="text-right p-3">{{ salesSummaryTotals.revenue | currencyFormat }}</b-td>
                    </b-tr>
                  </b-tfoot>
                  <b-tbody v-if="!isLoading && (!marketing.sales_summary || !marketing.sales_summary.length)">
                    <b-tr><b-td colspan="4" class="p-3 text-center">There are no sales in this range.</b-td></b-tr>
                  </b-tbody>
                  <b-tbody v-if="isLoading">
                    <b-tr>
                      <b-td colspan="4">
                        <div class="d-flex justify-content-center"><b-spinner variant="secondary"/></div>
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </b-card-body>
            </b-card>

            <b-card>
              <b-card-body class="p-1">
                <h4 class="text-muted mb-1">Sales Detail</h4>
                <p class="text-muted mb-3">Individual closed sales for the selected year, including their source, dates and products bought.</p>
                <b-table-simple responsive hover>
                  <b-thead>
                    <b-tr>
                      <b-th class="p-3">Customer</b-th>
                      <b-th class="p-3">Credited Source</b-th>
                      <b-th class="p-3">Lead Date</b-th>
                      <b-th class="p-3">Sale Date</b-th>
                      <b-th class="p-3">Products Bought</b-th>
                      <b-th class="text-right p-3">Revenue</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody v-if="!isLoading && marketing.sales && marketing.sales.length">
                    <b-tr v-for="(sale, ind) in marketing.sales" :key="`ms${ind}`">
                      <b-td class="p-3">
                        <router-link v-if="sale.customer_id" :to="{name: 'dash.customers.view', params: { id: sale.customer_id }}">{{ sale.customer_name }}</router-link>
                        <span v-else>{{ sale.customer_name }}</span>
                      </b-td>
                      <b-td class="p-3">{{ sale.source }}</b-td>
                      <b-td class="p-3">
                        <span v-if="sale.lead_at">{{ moment(sale.lead_at).format('DD/MM/YYYY') }}</span>
                        <span v-else class="text-muted">-</span>
                      </b-td>
                      <b-td class="p-3">{{ moment(sale.sale_at).format('DD/MM/YYYY') }}</b-td>
                      <b-td class="p-3">
                        <span v-if="sale.products && sale.products.length">{{ sale.products.join(', ') }}</span>
                        <span v-else class="text-muted">-</span>
                      </b-td>
                      <b-td class="text-right p-3">{{ sale.revenue | currencyFormat }}</b-td>
                    </b-tr>
                  </b-tbody>
                  <b-tfoot v-if="!isLoading && marketing.sales && marketing.sales.length">
                    <b-tr class="font-weight-bold">
                      <b-td colspan="5" class="p-3">Total</b-td>
                      <b-td class="text-right p-3">{{ salesDetailRevenueTotal | currencyFormat }}</b-td>
                    </b-tr>
                  </b-tfoot>
                  <b-tbody v-if="!isLoading && (!marketing.sales || !marketing.sales.length)">
                    <b-tr><b-td colspan="6" class="p-3 text-center">There are no sales in this range.</b-td></b-tr>
                  </b-tbody>
                  <b-tbody v-if="isLoading">
                    <b-tr>
                      <b-td colspan="6">
                        <div class="d-flex justify-content-center"><b-spinner variant="secondary"/></div>
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </b-card-body>
            </b-card>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Chart from 'chart.js/auto'
import momentMixin from '../../mixins/momentMixin'

export default {
  mixins: [momentMixin],
  data: () => ({
    isLoading: false,
    marketingYear: null,
    marketing: {
      lead_summary: [],
      sales_summary: [],
      leads: [],
      sales: [],
      trend: {
        labels: [],
        spend: [],
        revenue: [],
        leads: [],
        sales: [],
        monthly_spend: [],
        monthly_revenue: [],
        monthly_leads: [],
        monthly_sales: [],
        previous_year: null,
        previous_monthly_spend: [],
        previous_monthly_revenue: [],
        partial: []
      },
      product_category_revenue: {
        labels: [],
        datasets: []
      },
      product_category_sales: {
        labels: [],
        datasets: []
      }
    },
    marketingCumulativeChart: null,
    marketingMonthlyChart: null,
    productCategoryRevenueChart: null,
    productCategorySalesChart: null,
    marketingCumulativeView: 'chart',
    marketingMonthlyView: 'chart',
    productCategoryRevenueView: 'chart',
    productCategorySalesView: 'chart',
    googleCallLogFile: null,
    googleCallLogResult: null,
    isUploadingGoogleCallLog: false
  }),
  computed: {
    yearOptions () {
      const currentYear = this.moment().year()
      const years = []

      for (let year = currentYear; year >= 2025; year--) {
        years.push({ value: year, text: String(year) })
      }

      return years
    },
    productCategoryRevenueRows () {
      return this.productCategoryRows(this.marketing.product_category_revenue)
    },
    productCategorySalesRows () {
      return this.productCategoryRows(this.marketing.product_category_sales)
    },
    hasTrendComparison () {
      return Boolean(this.marketing.trend && this.marketing.trend.previous_year)
    },
    hasPartialTrendMonth () {
      return this.trendRows.some(row => row.partial)
    },
    trendRows () {
      const trend = this.marketing.trend || {}
      const labels = trend.labels || []
      const previousSpend = trend.previous_monthly_spend || []
      const previousRevenue = trend.previous_monthly_revenue || []
      let previousCumulativeSpend = 0
      let previousCumulativeRevenue = 0

      return labels.map((month, index) => {
        const spend = Number((trend.monthly_spend || [])[index] || 0)
        const revenue = Number((trend.monthly_revenue || [])[index] || 0)
        const previousMonthSpend = Number(previousSpend[index] || 0)
        const previousMonthRevenue = Number(previousRevenue[index] || 0)
        const cumulativeSpend = Number((trend.spend || [])[index] || 0)
        const cumulativeRevenue = Number((trend.revenue || [])[index] || 0)
        const cumulativeLeads = Number((trend.leads || [])[index] || 0)
        const cumulativeSales = Number((trend.sales || [])[index] || 0)

        previousCumulativeSpend += previousMonthSpend
        previousCumulativeRevenue += previousMonthRevenue

        return {
          month,
          spend,
          revenue,
          previousSpend: previousMonthSpend,
          previousRevenue: previousMonthRevenue,
          partial: Boolean((trend.partial || [])[index]),
          leads: Number((trend.monthly_leads || [])[index] || 0),
          sales: Number((trend.monthly_sales || [])[index] || 0),
          cumulativeSpend,
          cumulativeRevenue,
          cumulativeLeads,
          cumulativeSales,
          spendChange: this.hasTrendComparison ? spend - previousMonthSpend : null,
          spendChangePercent: this.hasTrendComparison ? this.percentageChange(spend, previousMonthSpend) : null,
          revenueChange: this.hasTrendComparison ? revenue - previousMonthRevenue : null,
          revenueChangePercent: this.hasTrendComparison ? this.percentageChange(revenue, previousMonthRevenue) : null,
          cumulativeSpendChange: this.hasTrendComparison ? cumulativeSpend - previousCumulativeSpend : null,
          cumulativeSpendChangePercent: this.hasTrendComparison ? this.percentageChange(cumulativeSpend, previousCumulativeSpend) : null,
          cumulativeRevenueChange: this.hasTrendComparison ? cumulativeRevenue - previousCumulativeRevenue : null,
          cumulativeRevenueChangePercent: this.hasTrendComparison ? this.percentageChange(cumulativeRevenue, previousCumulativeRevenue) : null
        }
      })
    },
    cumulativeTrendTotals () {
      const lastRow = this.trendRows[this.trendRows.length - 1]

      if (!lastRow) {
        return {
          spend: 0,
          revenue: 0,
          leads: 0,
          sales: 0,
          spendChange: null,
          spendChangePercent: null,
          revenueChange: null,
          revenueChangePercent: null
        }
      }

      return {
        spend: lastRow.cumulativeSpend,
        revenue: lastRow.cumulativeRevenue,
        leads: lastRow.cumulativeLeads,
        sales: lastRow.cumulativeSales,
        spendChange: lastRow.cumulativeSpendChange,
        spendChangePercent: lastRow.cumulativeSpendChangePercent,
        revenueChange: lastRow.cumulativeRevenueChange,
        revenueChangePercent: lastRow.cumulativeRevenueChangePercent
      }
    },
    trendTotals () {
      const previousSpend = this.sumValues(this.trendRows, 'previousSpend')
      const previousRevenue = this.sumValues(this.trendRows, 'previousRevenue')
      const spend = this.sumValues(this.trendRows, 'spend')
      const revenue = this.sumValues(this.trendRows, 'revenue')

      return {
        spend,
        revenue,
        leads: this.sumValues(this.trendRows, 'leads'),
        sales: this.sumValues(this.trendRows, 'sales'),
        spendChange: this.hasTrendComparison ? spend - previousSpend : null,
        spendChangePercent: this.hasTrendComparison ? this.percentageChange(spend, previousSpend) : null,
        revenueChange: this.hasTrendComparison ? revenue - previousRevenue : null,
        revenueChangePercent: this.hasTrendComparison ? this.percentageChange(revenue, previousRevenue) : null
      }
    },
    productCategoryRevenueTotal () {
      return this.sumValues(this.productCategoryRevenueRows, 'value')
    },
    productCategorySalesTotal () {
      return this.sumValues(this.productCategorySalesRows, 'value')
    },
    leadSummaryTotals () {
      return {
        leads: this.sumValues(this.marketing.lead_summary, 'leads_count'),
        sales: this.sumValues(this.marketing.lead_summary, 'sales_count'),
        spend: this.sumAdSpend(this.marketing.lead_summary),
        revenue: this.sumValues(this.marketing.lead_summary, 'revenue')
      }
    },
    salesSummaryTotals () {
      return {
        sales: this.sumValues(this.marketing.sales_summary, 'sales_count'),
        spend: this.sumAdSpend(this.marketing.sales_summary),
        revenue: this.sumValues(this.marketing.sales_summary, 'revenue')
      }
    },
    leadDetailRevenueTotal () {
      return this.sumValues(this.marketing.leads, 'revenue')
    },
    salesDetailRevenueTotal () {
      return this.sumValues(this.marketing.sales, 'revenue')
    }
  },
  created () {
    this.marketingYear = this.moment().year()
    this.fetchMarketing()
  },
  methods: {
    fetchMarketing () {
      this.isLoading = true

      return window.axios.get('/marketing/statistics', { params: { year: this.marketingYear } })
        .then(response => {
          this.marketing = response.data
          this.$nextTick(() => {
            this.renderVisibleMarketingProgressCharts()
            this.renderProductCategoryRevenueChart()
            this.renderProductCategorySalesChart()
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    uploadGoogleCallLog () {
      if (!this.googleCallLogFile) {
        return
      }

      const formData = new FormData()
      formData.append('call_log', this.googleCallLogFile)
      this.isUploadingGoogleCallLog = true
      this.googleCallLogResult = null

      return window.axios.post('/marketing/google-call-log', formData, {
        headers: {
          'content-type': 'multipart/form-data'
        }
      })
        .then(response => {
          this.googleCallLogResult = response.data
          this.googleCallLogFile = null
          this.$bvModal.hide('google-call-log-modal')
          return this.fetchMarketing()
        })
        .finally(() => {
          this.isUploadingGoogleCallLog = false
        })
    },
    productCategoryRows (chartData) {
      const labels = chartData.labels || []
      const values = chartData.datasets || []
      const total = values.reduce((sum, value) => sum + Number(value || 0), 0)

      return labels.map((label, index) => {
        const value = Number(values[index] || 0)

        return {
          label,
          value,
          percentage: total ? ((value / total) * 100).toFixed(1) : '0.0'
        }
      })
    },
    sumValues (rows, key) {
      return (rows || []).reduce((sum, row) => sum + Number(key ? row[key] || 0 : row || 0), 0)
    },
    sumAdSpend (rows) {
      return (rows || [])
        .filter(row => row.is_ad_source)
        .reduce((sum, row) => sum + Number(row.spend || 0), 0)
    },
    percentageChange (current, previous) {
      if (!previous) {
        return null
      }

      return ((current - previous) / previous) * 100
    },
    formatCurrencyChange (value) {
      if (value === null || value === undefined) {
        return '-'
      }

      const formatted = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(Math.abs(value))

      return `${value >= 0 ? '+' : '-'}${formatted}`
    },
    formatPercentageChange (value) {
      if (value === null || value === undefined) {
        return '-'
      }

      return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
    },
    setMarketingCumulativeView (view) {
      this.marketingCumulativeView = view

      if (view === 'chart') {
        this.$nextTick(() => this.renderMarketingTrendChart(this.$refs.marketingCumulativeChart, 'cumulative'))
      } else {
        this.destroyMarketingTrendChart('marketingCumulativeChart')
      }
    },
    setMarketingMonthlyView (view) {
      this.marketingMonthlyView = view

      if (view === 'chart') {
        this.$nextTick(() => this.renderMarketingTrendChart(this.$refs.marketingMonthlyChart, 'monthly'))
      } else {
        this.destroyMarketingTrendChart('marketingMonthlyChart')
      }
    },
    setProductCategoryRevenueView (view) {
      this.productCategoryRevenueView = view

      if (view === 'chart') {
        this.$nextTick(this.renderProductCategoryRevenueChart)
      }
    },
    setProductCategorySalesView (view) {
      this.productCategorySalesView = view

      if (view === 'chart') {
        this.$nextTick(this.renderProductCategorySalesChart)
      }
    },
    renderMarketingProgressCharts () {
      this.renderMarketingTrendChart(this.$refs.marketingCumulativeChart, 'cumulative')
      this.renderMarketingTrendChart(this.$refs.marketingMonthlyChart, 'monthly')
    },
    renderVisibleMarketingProgressCharts () {
      if (this.marketingCumulativeView === 'chart') {
        this.renderMarketingTrendChart(this.$refs.marketingCumulativeChart, 'cumulative')
      }

      if (this.marketingMonthlyView === 'chart') {
        this.renderMarketingTrendChart(this.$refs.marketingMonthlyChart, 'monthly')
      }
    },
    destroyMarketingTrendChart (chartKey) {
      if (this[chartKey]) {
        this[chartKey].destroy()
        this[chartKey] = null
      }
    },
    renderMarketingTrendChart (canvas, trendType) {
      if (!canvas || !this.marketing.trend) {
        return
      }

      const chartKey = trendType === 'cumulative' ? 'marketingCumulativeChart' : 'marketingMonthlyChart'

      if (this[chartKey]) {
        this[chartKey].destroy()
      }

      const valuePrefix = trendType === 'cumulative' ? '' : 'monthly_'
      const labelPrefix = trendType === 'cumulative' ? 'Total' : 'Monthly'
      const labels = this.marketingTrendChartValues('labels', trendType)

      this[chartKey] = new Chart(canvas, {
        type: 'line',
        options: {
          responsive: true,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            legend: {
              position: 'bottom'
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label || ''
                  const value = context.parsed.y || 0

                  if (context.dataset.yAxisID === 'currency') {
                    return `${label}: ${new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value)}`
                  }

                  return `${label}: ${value}`
                }
              }
            }
          },
          scales: {
            currency: {
              type: 'linear',
              position: 'left',
              ticks: {
                callback: (value) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value)
              }
            },
            count: {
              type: 'linear',
              position: 'right',
              grid: {
                drawOnChartArea: false
              },
              ticks: {
                precision: 0
              }
            }
          }
        },
        data: {
          labels,
          datasets: [
            {
              label: `${labelPrefix} Spend`,
              data: this.marketingTrendChartValues(`${valuePrefix}spend`, trendType),
              borderColor: '#dc3545',
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              yAxisID: 'currency',
              tension: 0.3
            },
            {
              label: `${labelPrefix} Revenue`,
              data: this.marketingTrendChartValues(`${valuePrefix}revenue`, trendType),
              borderColor: '#28a745',
              backgroundColor: 'rgba(40, 167, 69, 0.1)',
              yAxisID: 'currency',
              tension: 0.3
            },
            {
              label: `${labelPrefix} Leads`,
              data: this.marketingTrendChartValues(`${valuePrefix}leads`, trendType),
              borderColor: '#17a2b8',
              backgroundColor: 'rgba(23, 162, 184, 0.1)',
              yAxisID: 'count',
              tension: 0.3
            },
            {
              label: `${labelPrefix} Sales`,
              data: this.marketingTrendChartValues(`${valuePrefix}sales`, trendType),
              borderColor: '#6f42c1',
              backgroundColor: 'rgba(111, 66, 193, 0.1)',
              yAxisID: 'count',
              tension: 0.3
            }
          ]
        }
      })
    },
    marketingTrendChartValues (key, trendType) {
      const values = (this.marketing.trend || {})[key] || []

      if (trendType === 'cumulative') {
        return values
      }

      return values
    },
    productCategoryChartColors () {
      return [
        '#1e4841',
        '#357f71',
        '#4bb5a2',
        '#81cbbe',
        '#2d6d61',
        '#5dbcab',
        '#6fc4b5',
        '#3c9182',
        '#9dd8ce'
      ]
    },
    renderProductCategoryRevenueChart () {
      if (!this.$refs.productCategoryRevenueChart || !this.marketing.product_category_revenue) {
        return
      }

      if (this.productCategoryRevenueChart) {
        this.productCategoryRevenueChart.destroy()
      }

      this.productCategoryRevenueChart = new Chart(this.$refs.productCategoryRevenueChart, {
        type: 'doughnut',
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'bottom'
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || ''
                  const value = context.parsed || 0

                  return `${label}: ${new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value)}`
                }
              }
            }
          }
        },
        data: {
          labels: this.marketing.product_category_revenue.labels,
          datasets: [{
            label: 'Revenue',
            data: this.marketing.product_category_revenue.datasets,
            backgroundColor: this.productCategoryChartColors()
          }]
        }
      })
    },
    renderProductCategorySalesChart () {
      if (!this.$refs.productCategorySalesChart || !this.marketing.product_category_sales) {
        return
      }

      if (this.productCategorySalesChart) {
        this.productCategorySalesChart.destroy()
      }

      this.productCategorySalesChart = new Chart(this.$refs.productCategorySalesChart, {
        type: 'doughnut',
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'bottom'
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || ''
                  const value = context.parsed || 0

                  return `${label}: ${value}`
                }
              }
            }
          }
        },
        data: {
          labels: this.marketing.product_category_sales.labels,
          datasets: [{
            label: 'Sales',
            data: this.marketing.product_category_sales.datasets,
            backgroundColor: this.productCategoryChartColors()
          }]
        }
      })
    }
  },
  filters: {
    currencyFormat: (v) => new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP'
    }).format(v)
  }
}
</script>

<style scoped>
.product-category-chart {
  max-height: 320px;
}

.partial-month-row {
  background-color: rgba(75, 181, 162, 0.16);
}

</style>
